import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Spinner,
  Switch,
  Portal,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { DeleteIcon, CopyIcon, EditIcon } from "@chakra-ui/icons";
import { BsRobot, BsThreeDots } from "react-icons/bs";
import { AiFillStar } from "react-icons/ai";
import { MdBarChart } from "react-icons/md";
import { GoWorkflow } from "react-icons/go";
import { AnalystStats } from "build-ts/interface";
import { HeaderWithStats } from "services/MetadataServices";

interface AnalystCardProps {
  header: HeaderWithStats;
  defaultAnalystId: string;
  onAnalystSelection: (header: HeaderWithStats) => void;
  onRenameClick: (header: HeaderWithStats) => void;
  onDuplicateClick: (header: HeaderWithStats) => void;
  onDeleteClick: (header: HeaderWithStats) => void;
  onUpdateDefaultAnalyst: (header: HeaderWithStats) => void;
  isLoading?: boolean;
}

const AnalystCard: React.FC<AnalystCardProps> = ({
  header,
  defaultAnalystId,
  onAnalystSelection,
  onRenameClick,
  onDuplicateClick,
  onDeleteClick,
  onUpdateDefaultAnalyst,
  isLoading,
}) => {
  const isPrimary = defaultAnalystId === header.id;
  const stats = header.stats as AnalystStats;
  const isActive = stats?.num_live_jobs && stats.num_live_jobs > 0;
  const totalNoOfDataScopeItems =
    (stats?.num_metrics ?? 0) +
    (stats?.num_measures ?? 0) +
    (stats?.num_attributes ?? 0) +
    (stats?.num_timeframes ?? 0) +
    (stats?.num_benchmarks ?? 0);

  return (
    <Box
      key={header.id}
      bg="white"
      borderRadius="xl"
      borderWidth={isActive ? "3px" : "1px"}
      borderColor={isPrimary ? "purple.500" : isActive ? "green.600" : "gray.700"}
      boxShadow={isActive ? "0 0 0 2px rgba(72, 187, 120, 0.1)" : "none"}
      cursor="pointer"
      position="relative"
      overflow="hidden"
      _hover={{
        borderColor: isActive ? "green.600" : "purple.600",
        boxShadow: isActive ? "0 8px 16px rgba(72, 187, 120, 0.2)" : "0 8px 16px rgba(189, 67, 233, 0.12)",
        transform: "translateY(-4px)",
      }}
      onClick={() => onAnalystSelection(header)}
    >
      <Box p={5} display="flex" flexDirection="column" height="100%">
        <Flex align="center" justify="space-between" mb={3}>
          <HStack spacing={3} flex="1" align="center">
            <Icon as={BsRobot} boxSize={7} color="purple.600" />
            <Box flex="1" minW={0}>
              <Text fontSize="sm" fontWeight="700" color="gray.800" noOfLines={2} mb={0} align="left">
                {header.name || "Unnamed Analyst"}
              </Text>
            </Box>
          </HStack>

          <Menu>
            <MenuButton
              as={IconButton}
              icon={<Icon as={BsThreeDots} boxSize={5} />}
              variant="ghost"
              size="sm"
              color="gray.700"
              borderRadius="md"
              _hover={{ bg: "gray.100", color: "gray.700" }}
              onClick={e => e.stopPropagation()}
            />
            <Portal>
              <MenuList boxShadow="lg" borderRadius="lg">
                {!isPrimary && (
                  <MenuItem
                    onClick={e => e.stopPropagation()}
                    fontWeight="500"
                    fontSize="sm"
                    _hover={{ bg: "gray.50" }}
                  >
                    <Flex
                      align="center"
                      justify="start"
                      w="100%"
                      gap={3}
                      onClick={() => onUpdateDefaultAnalyst(header)}
                    >
                      <Switch
                        size="sm"
                        colorScheme="purple"
                        defaultChecked={false}
                        onClick={e => e.stopPropagation()}
                        mb={0}
                      />
                      <Text mb={0} fontWeight="500" fontSize="sm">
                        Set as primary
                      </Text>
                    </Flex>
                  </MenuItem>
                )}
                <MenuItem
                  icon={<EditIcon boxSize={4} />}
                  onClick={e => {
                    e.stopPropagation();
                    onRenameClick(header);
                  }}
                  fontWeight="500"
                  fontSize="sm"
                  _hover={{ bg: "gray.50" }}
                >
                  Rename
                </MenuItem>
                <MenuItem
                  icon={<CopyIcon boxSize={4} />}
                  onClick={e => {
                    e.stopPropagation();
                    onDuplicateClick(header);
                  }}
                  fontWeight="500"
                  fontSize="sm"
                  _hover={{ bg: "gray.50" }}
                >
                  Duplicate
                </MenuItem>
                <MenuItem
                  icon={<DeleteIcon boxSize={4} />}
                  onClick={e => {
                    e.stopPropagation();
                    onDeleteClick(header);
                  }}
                  color="red.600"
                  fontWeight="500"
                  fontSize="sm"
                  _hover={{ bg: "red.50" }}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>

        <Box mt="auto" pt={3} borderTop="1px" borderColor="gray.100">
          <Flex align="center" justify="space-between" wrap="wrap" gap={3} onClick={e => e.stopPropagation()}>
            <HStack spacing={4} flexWrap="wrap" align="center" flex="1" minW={0}>
              <Tooltip
                label={
                  <Box w="100%" textAlign="left" py={0} px={0}>
                    <VStack align="stretch" spacing={1} p={0} w="100%" textAlign="left">
                      {stats?.num_metrics && stats.num_metrics > 0 && (
                        <Text fontSize="xs" fontWeight="600" color="white" mb={0}>
                          Metrics: {stats.num_metrics}
                        </Text>
                      )}
                      {stats?.num_measures && stats.num_measures > 0 && (
                        <Text fontSize="xs" fontWeight="600" color="white" mb={0}>
                          Measures: {stats.num_measures}
                        </Text>
                      )}
                      {stats?.num_attributes && stats.num_attributes > 0 && (
                        <Text fontSize="xs" fontWeight="600" color="white" mb={0}>
                          Attributes: {stats.num_attributes}
                        </Text>
                      )}
                      {stats?.num_timeframes && stats.num_timeframes > 0 && (
                        <Text fontSize="xs" fontWeight="600" color="white" mb={0}>
                          Timeframes: {stats.num_timeframes}
                        </Text>
                      )}
                      {stats?.num_benchmarks && stats.num_benchmarks > 0 && (
                        <Text fontSize="xs" fontWeight="600" color="white" mb={0}>
                          Benchmarks: {stats.num_benchmarks}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                }
                openDelay={300}
                hasArrow
              >
                <HStack spacing={1} align="center" cursor="default">
                  <Icon as={MdBarChart} boxSize={5} color="gray.600" />
                  {isLoading ? (
                    <Spinner size="xs" color="purple.600" thickness="2px" />
                  ) : (
                    <Text fontSize="xs" fontWeight="600" color="gray.700" mb={0}>
                      {totalNoOfDataScopeItems > 0 ? totalNoOfDataScopeItems : "-"}
                    </Text>
                  )}
                </HStack>
              </Tooltip>
              <Tooltip
                label={
                  <Box w="100%" textAlign="left" py={0} px={0}>
                    <VStack align="stretch" spacing={1} p={0} w="100%" textAlign="left">
                      <Text
                        color="white"
                        fontSize="xs"
                        fontWeight="500"
                        lineHeight="1.35"
                        mb={0}
                        textAlign="left"
                        w="100%"
                      >
                        Active workflows: {stats?.num_live_jobs && stats.num_live_jobs > 0 ? stats.num_live_jobs : "—"}
                      </Text>
                      <Text
                        color="white"
                        fontSize="xs"
                        fontWeight="500"
                        lineHeight="1.35"
                        mb={0}
                        textAlign="left"
                        w="100%"
                      >
                        Total workflows: {stats?.total_jobs && stats.total_jobs > 0 ? stats.total_jobs : "—"}
                      </Text>
                    </VStack>
                  </Box>
                }
                openDelay={300}
                hasArrow
              >
                <HStack spacing={1} align="center" cursor="default">
                  <Icon as={GoWorkflow} boxSize={4} color="gray.600" />
                  {isLoading ? (
                    <Spinner size="xs" color="purple.600" thickness="2px" />
                  ) : (
                    <Text fontSize="xs" fontWeight="600" color="gray.700" mb={0}>
                      {stats?.total_jobs && stats.total_jobs > 0 ? stats.total_jobs : "—"}
                    </Text>
                  )}
                </HStack>
              </Tooltip>
              {isPrimary && (
                <HStack>
                  <HStack bg="purple.50" py={1} px={2} borderRadius="md" spacing={1.5}>
                    <Icon as={AiFillStar} boxSize={3} color="purple.400" aria-hidden />
                    <Text fontSize="sm" fontWeight="700" color="purple.600" mb={0}>
                      Primary
                    </Text>
                  </HStack>
                </HStack>
              )}
              {isActive && (
                <HStack bg="green.50" py={1} px={2} borderRadius="md" spacing={2}>
                  <Box
                    w="7px"
                    h="7px"
                    borderRadius="full"
                    bg="green.500"
                    flexShrink={0}
                    boxShadow="0 0 0 3px var(--chakra-colors-green-100), 0 0 10px var(--chakra-colors-green-200)"
                    aria-hidden
                  />
                  <Text fontSize="sm" fontWeight="700" color="green.700" mb={0}>
                    Live
                  </Text>
                </HStack>
              )}
            </HStack>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalystCard;
